import RouteError from '@/components/ui/route-error';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { LuMail } from 'react-icons/lu';
import { Box } from '@/components/ui/lib/box';
import { Card } from '@/components/ui/lib/card';
import { Container } from '@/components/ui/lib/container';
import { Flex } from '@/components/ui/lib/flex';
import { Heading } from '@/components/ui/lib/heading';
import { Input } from '@/components/ui/lib/input';
import { Link } from '@/components/ui/lib/link';
import { Separator } from '@/components/ui/lib/separator';
import { Text } from '@/components/ui/lib/text';
import { useBoolean } from '@/components/ui/lib/use-boolean';
import { DICTIONARY } from '@/config/dictionary';
import { APP_PATHS } from '@/config/routes/app-paths';
import { isDev } from '@/config/env';
import { ensureValidPath } from '@/utils/validate-path';
import FormField from '@/components/ui/form-field';
import IconBox from '@/components/ui/icon-box';
import PrimaryButton from '@/components/ui/primary-button';
import ResendEmailButton from '@/components/ui/resend-email-button';
import { useResendCooldown } from '@/hooks/use-resend-cooldown';
import { resendActivationSchema } from '@/types/form-schemas';
import { useResendActivationEmail } from '@/features/activation/api/activation-api';

const pendingActivationSearchSchema = z.object({
  email: z.string().optional(),
});

/**
 * Fallback for a cold arrival — a bookmark, or a different device — where sign-in never supplied
 * the address. Reports the same outcome either way, so it cannot be used to test which addresses
 * are registered.
 */
const ResendByEmailForm = () => {
  const d = DICTIONARY.auth.pendingActivation;
  const [hasRequested, setHasRequested] = useBoolean(false);
  const { isCooling, secondsRemaining, start } = useResendCooldown();
  const { mutateAsync: resend, isPending: isResending } = useResendActivationEmail();

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      if (isCooling) return;

      try {
        await resend({ email: value.email });
      } catch (error) {
        // Swallowed on purpose: the UI must not distinguish a failed send from a successful one.
        // Still logged in dev so a genuinely broken endpoint stays visible while working on it.
        if (isDev) console.error('[resend activation error]', error);
      } finally {
        setHasRequested.on();
        start();
      }
    },
  });

  return (
    <Box w="full">
      <Text fontSize="sm" className="text-muted" mb="4">
        {d.emailPrompt}
      </Text>

      <Box
        as="form"
        onSubmit={(e: React.SyntheticEvent<HTMLElement>) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <Flex direction="column" gap="4">
          <form.Field name="email" validators={{ onSubmit: resendActivationSchema.shape.email }}>
            {(field) => (
              <FormField field={field} label={DICTIONARY.common.emailLabel}>
                <Input
                  type="email"
                  autoComplete="email"
                  value={field.state.value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.handleChange(e.target.value);
                  }}
                  onBlur={field.handleBlur}
                />
              </FormField>
            )}
          </form.Field>

          {hasRequested && (
            <Box>
              <Text fontSize="sm" className="text-accent">
                {d.neutralResult}
              </Text>
              <Text fontSize="sm" className="text-muted" mt="1">
                {DICTIONARY.common.checkJunkFolder}
              </Text>
            </Box>
          )}

          <PrimaryButton
            type="submit"
            size="sm"
            w="full"
            loading={isResending}
            disabled={isCooling || isResending}
          >
            {isCooling
              ? DICTIONARY.auth.checkEmail.resendCooldown(secondsRemaining)
              : DICTIONARY.auth.checkEmail.resend}
          </PrimaryButton>
        </Flex>
      </Box>
    </Box>
  );
};

const PendingActivationPage = () => {
  const navigate = useNavigate();
  const { email } = Route.useSearch();
  const d = DICTIONARY.auth.pendingActivation;
  const { mutateAsync: resend } = useResendActivationEmail();

  const handleResend = async () => {
    await resend({ email: email ?? '' });
  };

  const handleBackToSignIn = () => {
    void navigate({ to: ensureValidPath(APP_PATHS.home), search: { tab: 'signin' } });
  };

  return (
    <Box
      as="section"
      minH="calc(100vh - 72px)"
      className="bg-page"
      display="flex"
      alignItems="center"
    >
      <Container maxW="lg" px={{ base: '6', sm: '8' }} py="16">
        <Card.Root className="bg-surface border-rim">
          <Card.Body p="10">
            <Flex direction="column" alignItems="center" textAlign="center" gap="6">
              <IconBox icon={LuMail} size="lg" colorScheme="sky" />

              <Box>
                <Heading as="h1" fontSize="2xl" fontWeight="700" className="text-base" mb="2">
                  {d.heading}
                </Heading>
                <Text fontSize="sm" className="text-muted" maxW="sm">
                  {d.body}
                </Text>
                <Text fontSize="sm" className="text-muted" maxW="sm" mt="2">
                  {DICTIONARY.common.checkJunkFolder}
                </Text>
                {email && (
                  <Text fontSize="sm" fontWeight="semibold" className="text-base" mt="3">
                    {email}
                  </Text>
                )}
              </Box>

              <Separator className="border-rim" />

              <Box w="full">
                <Text fontSize="sm" fontWeight="semibold" className="text-base" mb="4">
                  {d.expiredPrompt}
                </Text>

                {email ? <ResendEmailButton onResend={handleResend} /> : <ResendByEmailForm />}
              </Box>

              <Link
                as="button"
                onClick={handleBackToSignIn}
                fontSize="sm"
                className="text-muted hover:text-accent"
              >
                {d.backToSignIn}
              </Link>
            </Flex>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
};

export const Route = createFileRoute('/pending-activation')({
  validateSearch: pendingActivationSearchSchema,
  component: PendingActivationPage,
  errorComponent: RouteError,
});
